import { Radio } from 'components/common/Radio/Radio';
import { FacetAccordion, getVariantLayout } from 'components/search/FacetAccordion/FacetAccordion';
import { selectedValueNamesClasses, valueRowClasses } from 'components/search/FacetAccordion/FacetAccordion.classes';
import { useFilteredSortOptions } from 'hooks/useFilteredSortOptions';
import { useSortSelectedValue } from 'hooks/useSortSelectedValue';
import { filterSortOptions } from 'utils/filterSortOptions';
import useMartyContext from 'hooks/useMartyContext';
import type { FacetDataType } from 'types/search';

const SORT_SECTION_LABEL = 'Sort';

/**
 * `FacetAccordion.Item` requires a `facet: NavigationItem` to drive its header/chrome.
 * Sort is not a backend facet, so we define a placeholder sort single-select facet for the
 * accordion header text and a11y. Its `values` stay empty — the radio options are rendered
 * directly from the sort hooks below (not through ValueList/Value/RadioFacet, which are
 * coupled to real facet data and force a `(count)`).
 */
const SORT_FACET: FacetDataType = {
  type: 'single',
  facetField: 'sort',
  facetFieldDisplayName: SORT_SECTION_LABEL,
  facetUrl: '',
  isExpanded: false,
  multiSelect: false,
  values: []
};

export interface SortSectionProps {
  selectedSort: Record<string, string>;
  isBestForYouSortEligible: boolean;
  onSortSelected: React.ChangeEventHandler<HTMLInputElement>;
  isLoading: boolean;
}

export const SortSection = ({ selectedSort, isBestForYouSortEligible, onSortSelected, isLoading }: SortSectionProps) => {
  const { testId } = useMartyContext();

  const sortOptions = useFilteredSortOptions();
  const options = filterSortOptions(sortOptions, !!isBestForYouSortEligible);
  const selectedValue = useSortSelectedValue(options, selectedSort);
  const ulClassName = getVariantLayout('radio');

  const handleSortSelected: React.ChangeEventHandler<HTMLInputElement> = event => {
    // Like the filter handlers, no-op while a fetch is in flight (isLoading) so a rapid tap
    // can't fire an overlapping request.
    if (isLoading) {
      return;
    }

    // Delegate to the shared sort handler, which refetches results in the background.
    // Unlike SortDrawer, we do not close the drawer here — it stays open like the filter sections.
    onSortSelected(event);
  };

  return (
    <FacetAccordion.Item value="sort" facet={SORT_FACET} data-test-id={testId('facetGroup-Sort')}>
      <FacetAccordion.Trigger data-test-id-facet-head-name={SORT_SECTION_LABEL} hasSubtext>
        <FacetAccordion.DisplayName data-test-id={testId('facetGroupHeadTestId')} />
        {/* Current-sort subtext; shares FacetAccordion.SelectedValueNames styling via the constant. */}
        <span className={selectedValueNamesClasses} data-test-id={testId('sortSelectedValue')}>
          {selectedValue?.label}
        </span>
      </FacetAccordion.Trigger>

      <FacetAccordion.Content>
        {/* Mirror the facet radio rows (FacetAccordion.Value) */}
        <ul className={ulClassName}>
          {options.map(({ eventLabel, label, value, ariaLabel }) => (
            <li key={value} className={valueRowClasses}>
              <Radio
                name="mobile-sort"
                value={value}
                checked={selectedValue?.value === value}
                onChange={handleSortSelected}
                label={label}
                data-label={eventLabel || label}
                aria-label={ariaLabel || label}
                data-test-id={testId(`sortOption-${value}`)}
              />
            </li>
          ))}
        </ul>
      </FacetAccordion.Content>
    </FacetAccordion.Item>
  );
};
